import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Environment } from "@core/config/environment";
import { Path } from "@core/routing/path";
import { Profile } from "@domain/user/profile";
import { fullName } from "@domain/user/utils";
import { getBanner } from "@utils/banner";
import { gap } from "@ui/styles/utils";
import { Card } from "@ui/components/containers/card";
import { Clamp } from "@ui/components/containers/clamp";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { row } from "@ui/components/layout";
import { FollowButton } from "./followButton";
import { ProfileBanner } from "./profileBanner";
import { ProfileLocation } from "./profileLocation";
import { ProfilePicture } from "./profilePicture";

interface SuggestedProfileThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	profile: Profile;
	reachable?: boolean;
}
export const SuggestedProfileThumbnail: React.FC<SuggestedProfileThumbnailProps> = ({
	profile,
	reachable = true,
	...htmlProps
}) => {
	const { id, banner, avatar, description, categories, countryCode, contentCount } = profile;
	return (
		<Container {...htmlProps}>
			<ProfileBanner picture={banner} defaultUrl={!banner ? getBanner(id) : undefined} height={80} />
			<ProfilePictureWrapper disabled={!reachable}>
				<Link to={Path.Profile(profile)}>
					<ProfilePicture
						alt={I18n.t("seo.profile.picture", { name: fullName(profile) })}
						picture={avatar}
						size={70}
						shadowed
					/>
				</Link>
			</ProfilePictureWrapper>
			<div>
				{reachable ? (
					<Link to={Path.Profile(profile)}>
						<Name>{fullName(profile)}</Name>
					</Link>
				) : (
					<Name>{fullName(profile)}</Name>
				)}
				{!!description && <Description>{description.toString()}</Description>}
			</div>
			{countryCode && <StyledProfileLocation countryCode={countryCode} />}
			<CategoriesWrapper>
				<Categories>
					{categories.map((category) => (
						<CategoryView key={category}>{I18n.t(`category.${category}`)}</CategoryView>
					))}
				</Categories>
			</CategoriesWrapper>
			<Grow />
			{reachable && categories.length > 3 && (
				<SeeMoreLink to={id ? Path.Profile(profile) : ""}>
					<span>{I18n.t("search.user.see_more_categories")}</span>
				</SeeMoreLink>
			)}
			{!!contentCount && <ContentCount>{I18n.t("profile.content.published", { count: contentCount })}</ContentCount>}
			<FollowButton profile={profile} isBig />
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	position: relative;
	padding: 42px 20px 20px 20px;

	${gap(15)};
	display: flex;
	flex-direction: column;

	button {
		margin-top: 5px;
		align-self: flex-start;
	}
`;

const ProfilePictureWrapper = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>`
	pointer-events: ${({ disabled }) => (disabled ? "none" : "all")};
	position: relative;
	${row(0, "space-between", "flex-end")};
`;

const Name = styled(Clamp)`
	${({ theme }) => theme.textStyles.primaryBold};
	font-size: 1.25rem;
	margin-top: 5px;
	word-break: break-word;
`;

const Description = styled(Clamp)`
	${({ theme }) => theme.textStyles.primary};
	color: ${({ theme }) => theme.colors.textSecondary};
	word-break: break-word;
	margin-top: 3px;
	max-width: 320px;
`;

const StyledProfileLocation = styled(ProfileLocation)`
	position: absolute;
	top: 90px;
	right: 20px;
	max-width: 120px;
	word-break: break-all;
	align-items: center;
`;

const CategoriesWrapper = styled.div`
	position: relative;
	margin: -12px;
	overflow-y: hidden;

	&::after {
		position: absolute;
		left: 0;
		right: 0;
		top: 70%;
		bottom: 0;
		content: "";
		background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 1) 100%);
	}
`;

const Categories = styled.div`
	display: flex;
	flex-wrap: wrap;
	padding: 12px;
	max-height: 130px;
`;

const CategoryView = styled(Card)`
	${({ theme }) => theme.textStyles.light};
	border: none;
	box-shadow: ${Environment.APP_VARIANT === "jaangal" ? "none" : "0 4px 16px 0 red"};
	background-color: ${({ theme }) =>
		Environment.APP_VARIANT === "jaangal" ? theme.colors.simplegray : theme.colors.white};
	border-top-left-radius: ${Environment.APP_VARIANT === "jaangal" ? "none" : 0};
	margin: 3px;
	padding: 8px;
`;

const SeeMoreLink = styled(Link)`
	font-size: 0.8125rem;
	color: ${({ theme }) => theme.colors.textPrimary};
	text-decoration: underline;
	text-align: start;
	margin-top: -5px;
`;

const Grow = styled.div`
	flex: 1;
	margin-bottom: 0 !important;
`;

const ContentCount = styled.div`
	${({ theme }) => theme.textStyles.text};
`;
